/**
 * Similar Patients Analytics component
 */
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, OnDestroy, OnInit} from "@angular/core";
import {Widget} from "@ispm/widgets/decorator/widget.decorator";
import {IWidgetItem} from "@ispm/widgets/interfaces/widget-item.interface";
import {IdynamicFormItem} from "@ispm/dynamic-form/interfaces/IdynamicFormItem";
import {
  IAnalyticsChartDataSource,
  IAnalyticsChartDataTarget
} from "@ispm/widgets/components/analytics-charts/interfaces/analytics-charts-data.interface";
import * as _ from 'lodash';
import {delay, filter, flatMap, map, take, takeUntil} from 'rxjs/operators';
import {FormGroup} from "@angular/forms";
import {combineLatest, Observable, Subject} from "rxjs";
import {InterpolateService} from '@ispm/shared/services/interpolate.service';
import {CoreActionCreatorService} from "@ispm/core/services/core-action-creator.service";
import {IspmHttpClient} from "@ispm/core/services/ispm-http-client";
import {select, Store} from "@ngrx/store";
import {
  buildSimilarPatientsKeys,
  FetchCurrentPatientSimilarDataAction,
  NO_SP_CHANGE,
  selectCurrentPatientData,
  selectSimilarPatientsSearchResult,
  SimilarPatientsDataState
} from '@ispm-client/modules/similar-patients/store';
import {
  ActionTypes,
  SearchSimilarPatientsDataAction,
  StateTypes
} from "@ispm-client/modules/similar-patients/store/actions/similar-patients.actions";
import {selectSimilarPatientsError} from "@ispm-client/modules/similar-patients/store/selectors/selectors";
import {FilterService} from "../../../app/filter/services/filter.service";
import {FILTER_SUBTYPE, FILTER_TYPE} from "@ispm-client/modules/similar-patients/services/config";
import {TranslateService} from '@ngx-translate/core';
import "../source-js/utils/configHandler.js";
import "../source-js/utils/configMenuHandler.js";
import "../source-js/utils/dataHandler.js";
import "../source-js/utils/drawUtils.js";
import "../source-js/utils/statePropsHandler.js";
import "../source-js/utils/toolTipHandler.js";
import "../source-js/utils/updateHandler.js";
import "../source-js/paralelPlot/d3_cohortSelector.js";
import "../source-js/paralelPlot/d3_PP_Canvas.js";
import "../source-js/paralelPlot/d3_PP_CategoryColumn.js";
import "../source-js/paralelPlot/d3_PP_ContinuousColumn.js";
import {updateSimilarPatients} from "@ispm-client/modules/similar-patients/source-js/init";
import {displayRootElement} from "@ispm-client/modules/similar-patients/source-js/utils/dataHandlers/ispmDataHandler";
import {NotificationMessageService} from "@ispm/core/components/customise-notification/notifications-message.service";
import {RouterManagerService} from "@ispm/core/services/router-manager.service";

const updateHandler = require("@ispm-client/modules/similar-patients/source-js/utils/updateHandler");

@Component({
  selector: 'similar-patients-analytics',
  templateUrl: './similar-patients-analytics.component.html',
  styleUrls: ['./similar-patients-analytics.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
@Widget('SimilarPatientsAnalytics')
export class SimilarPatientsAnalyticsComponent implements OnInit, OnDestroy, IWidgetItem, IAnalyticsChartDataSource {

  // need to set internal id of the component
  @HostBinding('id') elementId: string = "similarPatientsAnalyticsFormInternalID";

  public widgetUpdateValueCallback?: (value) => void;

  private _widgetFormInstance: FormGroup;
  private toggler$: Subject<void> = new Subject<void>();
  private similarPatientsStoreSubject$: Subject<void> = new Subject<void>();
  private filterSubject$: Subject<any> = new Subject();
  private loaderCounter: number = 0;
  private maxDisplayedColumns: number = 20;

  set widgetFormInstance(widgetFormInstance: FormGroup) {

    if (this._widgetFormInstance === widgetFormInstance) return;

    this._widgetFormInstance = widgetFormInstance;

    // listen to form's data changes
    this.registerChangeListeners();

  }

  private _widgetConfig;
  private _widgetData: any = {};
  private _currentPatientData: any = {};
  private patientsQueryParams: string;
  private patientsQueryParamsObject: any;
  private internalState = {
    init: false,
    widgetConfigChange: false,
    widgetDataChange: false,
    filterConfigLoaded: false,
    listened: false,
    done: false,
    searchInProgress: false
  };
  //========================================================================================================

  constructor(private httpService: IspmHttpClient,
              private coreActionCreator: CoreActionCreatorService,
              private similarPatientDataStore$: Store<SimilarPatientsDataState>,
              private filterService: FilterService,
              private translate: TranslateService,
              private routerManagerService: RouterManagerService,
              private _changeDetector: ChangeDetectorRef,
              private notificationMessageService: NotificationMessageService) {

  }
  //========================================================================================================

  /**
   * set widget config
   * @param _itemConfig
   */
  set widgetConfig(_itemConfig: IdynamicFormItem) {

    // if configuration data was not changed, exit
    if (_.isEqual(this._widgetConfig, _itemConfig.widgetPayload)) return;

    this._widgetConfig = _itemConfig.widgetPayload;
    if (! this._widgetConfig) this._widgetConfig = {};

    // extract the configuration relating to the patients' data
    this._widgetConfig.patientsConfig = this.extractPatientsConfig();

    // listen to form's data changes
    this.registerChangeListeners();

    // init graphs on configuration change
    this.internalState.widgetConfigChange = true;
    this.initDataHandler();
  }
  //========================================================================================================

  /**
   * set widget data
   * @param widgetData
   */
  set widgetData(widgetData: any) {
    // Note: widget's data is built and managed internally
  }
  //========================================================================================================

  ngOnInit() {

    // clear first previous preserved data, if any. This is to ensure that storage data holds the appropriate
    //  information for the current user

    // @todo should clear only if user's organization had changed?????
    this.similarPatientDataStore$.dispatch({
      type: ActionTypes.CLEAR_SIMILAR_PATIENTS_DATA
    });

    this.loadCurrentPatientData();

    // start listening to the similar-patients storage
    this.listenToSimilarPatientsStorage();

    // init graphs
    this.internalState.init = true;
    this.initDataHandler();
  }
  //========================================================================================================

  ngOnDestroy() {

    this.toggler$.next();
    this.toggler$.complete();

    this.similarPatientsStoreSubject$.next();
    this.similarPatientsStoreSubject$.complete();

    this.filterSubject$.next();
    this.filterSubject$.complete();

  }
  //========================================================================================================

  registerChartDataTarget (target: IAnalyticsChartDataTarget) {
    updateHandler.externalDataTarget = target;

    this.internalState.listened = true;
    this.initDataHandler();
  }
  //========================================================================================================

  private registerChangeListeners() {

    // register as a listener on any configured field
    if (this._widgetFormInstance &&
      this._widgetConfig && this._widgetConfig.listenToFields) {

      // clear current listeners
      this.toggler$.next();

      _.each(this._widgetConfig.listenToFields, fieldName => {

        if (this._widgetFormInstance.controls.hasOwnProperty(fieldName)) {

          this._widgetFormInstance.controls[fieldName].valueChanges
            .pipe(takeUntil(this.toggler$.asObservable()))
            .subscribe(fieldData => {
              this.onFormDataChanges(fieldData, fieldName)
          });
        }
      });

    }
  }
  //========================================================================================================

  initDataHandler() {

    // data cannot be initialized in the following cases:

    // (a) filter's configuration had NOT been completely loaded
    if (! this.internalState.filterConfigLoaded) return;

    // (b) widget config/data initially had NOT been completely loaded
    if (! this.internalState.done &&
        (! this.internalState.widgetConfigChange || ! this.internalState.widgetDataChange)) return;

    // otherwise, init data-handler only if:

    // if charts are enabled by configuration, init data-handler only after charts' listener has been registered
    let chartsEnabled = (!!this._widgetData.chartsConfig);

    // (a) charts are enabled by configuration & charts' listener is been registered
    //     -OR-
    // (b) charts are disabled by configuration
    let mayInit = (chartsEnabled && this.internalState.listened) || (! chartsEnabled);

    // (c) configuration/data had changed & data-handler was already initialized
    //     (if not initialised yet, it will be invoked at ngOnInit)
    mayInit = mayInit || this.internalState.done &&
                         (this.internalState.widgetConfigChange || this.internalState.widgetDataChange);

    if (mayInit) {

      // set current patient as the active patient
      let activePatientId = '';
      if (this._currentPatientData) {
        activePatientId = this._currentPatientData.id;
      }
      this._widgetData.parallelPlotConfig.activePatient = { "id": activePatientId};

      // call JS code to update new data. Method returns an error, if update failed due to an internal error
      this.setLoader(true);

      const self = this;
      this.invokeLater(() => {

          let errorOnUpdate = updateSimilarPatients(self._widgetData.parallelPlotConfig, 'ISPM', self._widgetData.patientsData);

          self.setLoader(false);

          if (errorOnUpdate) {
            console.error("Failed to update patients data: " + errorOnUpdate);
            self.notificationMessageService.error("Failed to update patients data");

          } else {
            self._changeDetector.markForCheck();
            self._changeDetector.detectChanges();

          }

          // mark that data-handler was initialized
          self.internalState.done = true;

          // reset configuration/data change flags
          self.internalState.widgetDataChange = false;
          self.internalState.widgetConfigChange = false;
        });

    }

  }
  //========================================================================================================

  getChartsDataSource() {
    return this;
  }
  //========================================================================================================

  onFormDataChanges(fieldData, fieldName) {

    let currentValue = _.get(this._widgetData.formData, fieldName);

    // if no relevant data was changed, exit
    if (_.isEqual(currentValue, fieldData)) return;

    if (! this._widgetData.formData) this._widgetData.formData = {};
    this._widgetData.formData[fieldName] = fieldData;
    //-----------------------------------------------------------------------------------------------------

    // build the query to run to get the patients
    let queryParams = this._widgetConfig.patientsConfig.queryParams;
    if (queryParams) {
      queryParams = InterpolateService.interpolate(queryParams, this._widgetData.formData);
    }

    // if no relevant data was changed, exit
    if (_.isEqual(queryParams, this.patientsQueryParams)) return;

    this.patientsQueryParams = queryParams;
    this.patientsQueryParamsObject = JSON.parse(queryParams);
    //-----------------------------------------------------------------------------------------------------

    // if no data is set - reset patients's fields (MUST check it after comparing queryParams)
    if (!fieldData) {
      this.onReadPatientsData(true);
      return;
    }

    // before fetching new data, hide current display to prevent confusion of the relation of the current data to the
    //  search parameters
    displayRootElement(false);

    // new data is set: execute query to get patients
    this.setLoader(true);

    // execute data change event outside of the change of field's value. This is to allow first the field's value
    //  to be changed at the UI and then other UI events will be performed
    const self = this;
    this.invokeLater(() => {
      self.internalState.searchInProgress = true;

      self.similarPatientDataStore$.dispatch(new SearchSimilarPatientsDataAction({ searchParams: self.patientsQueryParamsObject}));
    });

  }
  //========================================================================================================

  listenToSimilarPatientsStorage() {

    // clear current listeners
    this.similarPatientsStoreSubject$.next();

    const self = this;
    //-------------------------------------------------------------------------------

    // listen to changes of the current patient
    this.similarPatientDataStore$.pipe(
      select(selectCurrentPatientData),
      takeUntil(this.similarPatientsStoreSubject$.asObservable()))
      .subscribe(currentPatientData => {

        if (currentPatientData === NO_SP_CHANGE) return;
        console.info("received new currentPatientData");

        // update the received current patient
        self._currentPatientData = currentPatientData;

        // re-build graph
        self.onReadPatientsData();

    });
    //-------------------------------------------------------------------------------

    // listen to changes of the similar patients data
    this.similarPatientDataStore$.pipe(
      select(selectSimilarPatientsSearchResult),
      takeUntil(this.similarPatientsStoreSubject$.asObservable()))
      .subscribe(similarPatientsMap => {

        // refer to received patients only on data loading OR if similarPatientsMap is changed
        if (! self.internalState.searchInProgress || similarPatientsMap === NO_SP_CHANGE) return;

        // search the results for the current requested search-keys
        let similarPatientsData: any[];
        let similarPatientsSize = 0;

        if (!!similarPatientsMap && similarPatientsMap.size > 0) {
          let keyInMap = buildSimilarPatientsKeys(self.patientsQueryParamsObject);
          similarPatientsData = similarPatientsMap.get(keyInMap);
          similarPatientsSize = _.size(similarPatientsData);

          console.info("received " + similarPatientsSize + " similarPatientsData for keys: " + keyInMap);
        }

        // update the received patients
        self._widgetData.patientsData = similarPatientsData;

        self.onSearchPatientsComplete();

        if (similarPatientsSize == 0) {
          self.notificationMessageService.info('No relevant patients data is found!');
        }

    });
    //-------------------------------------------------------------------------------

    // listen to received errors while fetching similar-patients data
    this.similarPatientDataStore$.pipe(
      select(selectSimilarPatientsError),
      takeUntil(this.similarPatientsStoreSubject$.asObservable()))
      .subscribe(error => {

        if (error &&
            error.stateType === StateTypes.SEARCH_SIMILAR_PATIENTS) {

          console.error('error ' + JSON.stringify(error.error) + ' during fetching resource: ' + self.patientsQueryParams);
          self.notificationMessageService.error('failed to get patients data');

          self.onSearchPatientsComplete();
        }

    });
    //-------------------------------------------------------------------------------

    // listen to changes of the current filter's configuration AND applied filter's parameters
    this.filterService.loadFiltersList(FILTER_TYPE, FILTER_SUBTYPE, FILTER_SUBTYPE).pipe(
      flatMap(() => {

        // Note: listen to any change of the filter's configuration or applied filter's parameters
        //       and combine the results of both.
        //       Note that the applied formData is preserved also after refresh
        return combineLatest(
            [self.filterService.getCurrentSelectedFilterFromStore(),
                    self.filterService.getCurrentSelectedFilterConfigFromStore(),
                    self.filterService.getCurrentSelectedFormDataFromStore()]).pipe(

          map(([selectedFilter, filterConfig, currentFormData]) => (
            {
              selectedFilter: selectedFilter,
              filterConfig: filterConfig,
              currentFormData: currentFormData
            }
          )));
      })
      ).pipe(
        takeUntil(self.similarPatientsStoreSubject$.asObservable())

      ).subscribe((filterResult) => {
        const selectedFilter = filterResult.selectedFilter;
        const filterConfig = filterResult.filterConfig;
        const currentFormData = filterResult.currentFormData;

        console.info("received filter's configuration: selectedFilter= " + (!!selectedFilter)
            + ", filterConfig= " + (!!filterConfig)
            + ", currentFormData= " + (!!currentFormData));

        // update the received configuration
        if (selectedFilter && filterConfig && currentFormData) {
          self.onReadFilterConfiguration(selectedFilter.filter, filterConfig, currentFormData);
        }
      }
    );
    //-------------------------------------------------------------------------------

  }

  private loadCurrentPatientData() {
    this.similarPatientDataStore$
        .pipe(
            select(selectCurrentPatientData),
            filter((currentPatientData) => !currentPatientData),
            take(1)
        )
        .subscribe((currentPatientData) => {
          this.similarPatientDataStore$.dispatch(
              new FetchCurrentPatientSimilarDataAction({ patientId: this.routerManagerService.parameters.patientId }));
        });
  }

//========================================================================================================

  /**
   * read the configuration of the 'patients'
   */
  extractPatientsConfig(): any {

    let patientsConfig: any = {};

    if (this._widgetConfig.formConfig) {

      let config = _.filter(this._widgetConfig.formConfig, config => {
        return config.name === "patients";
      });

      if (_.size(config) > 0) {
        let queryParams: any = _.get(config[0], 'queryParams');

        if (queryParams) {
          queryParams = JSON.stringify(queryParams);
        }
        patientsConfig.queryParams = queryParams;
      }
    }

    return patientsConfig;
  }
  //========================================================================================================

  onReadPatientsData(reset: boolean = false) {

    // if still on data loading - wait until all data is received
    if (this.internalState.searchInProgress) return;
    //---------------------------------------------------------------------

    if (reset) {
      this._widgetData.patientsData = [];

    } else if (this._widgetData.patientsData &&
               this._currentPatientData) {

      // add the current patient's data to the list of patients
      // Note: add it to the beginning of the array for faster search of the active patient in the code
      this._widgetData.patientsData.unshift(this._currentPatientData);

    // data had not been changed yet - exit
    } else {
      return;
    }

    // indicate that data had changed and re-invoke the rebuild method
    this.internalState.widgetDataChange = true;
    this.initDataHandler();

    // inform parent on widget's patientsData changes
    if (this.widgetUpdateValueCallback) {
      this.widgetUpdateValueCallback.apply(this._widgetData.patientsData);
    }

  }
  //========================================================================================================

  private onSearchPatientsComplete() {

    // turn-off loading flag but keep loader state-on while building graph
    this.internalState.searchInProgress = false;

    // re-build graph
    this.onReadPatientsData();

    this.setLoader(false);

  }
  //========================================================================================================

  private onReadFilterConfiguration(selectedFilter, filterConfig, currentFormData) {

    const colsAttributes: any = filterConfig.columns.attributes;
    const colsData: any = filterConfig.columns.data;
    const colsGeneralData = filterConfig.columns.general;
    const chartsAttributes: any = filterConfig.charts.attributes;
    const chartsData: any = filterConfig.charts.data;
    //------------------------------------------------------------------------------------------------------------------

    // go over all configured filter's parameters to display
    let inputCols = {};
    let outputCols = {};
    let numActiveCols = 0;

    // if filter's configuration had not been loaded yet, use the formData of the current selected filter and not the last
    //  applied data. This will align the filter selection on the filter-panel with the SP view
    if (! this.internalState.filterConfigLoaded) {
      currentFormData = selectedFilter.formData;
    }

    // extract from the given currentFormData the input/output parameters
    const formDataObject = this.extractFilterFormData(currentFormData);

    // read configured input parameters to display
    _.forEach(formDataObject.input, queryParamId => {

        // add parameter to the list of input columns
        let paramObj = this.addQueryInputParam(queryParamId, colsData, inputCols, true);

        // if parameter is valid, increment the number of active input columns
        if (paramObj) numActiveCols++;
    });

    // read configured output parameters to display
    _.forEach(formDataObject.output, queryParamId => {

      // add parameter to the list of output charts
      let paramObj = this.addQueryParam(queryParamId, chartsData, outputCols, "colHeader", "colId");

      // if parameter is valid, need to add it also to the list of input columns (as an inactive parameter)
      if (paramObj && ! inputCols[queryParamId]) {
        this.addQueryInputParam(queryParamId, colsData, inputCols, false);
      }

    });
    //------------------------------------------------------------------------------------------------------------------

    // at least 2 input parameters must be provided - can't display a parallel-plot without it
    if (numActiveCols < 2) {
      this.notificationMessageService.warning("Insufficient number of input parameters are selected");
      return;
    }

    // get the configured maximum columns to display
    if (colsAttributes['maxDisplayedColumns']) {
      this.maxDisplayedColumns = colsAttributes['maxDisplayedColumns'];
      delete colsAttributes['maxDisplayedColumns'];
    }

    // check that number of active columns does not exceed the maximum number
    if (numActiveCols > this.maxDisplayedColumns) {
      this.notificationMessageService.warning("Cannot display more than " + this.maxDisplayedColumns + " input parameters");
      return;
    }

    let parallelPlotConfig: any = {
      columns: inputCols
    };

    // set general input parameters
    this.addGeneralConfigAttributes(colsAttributes, parallelPlotConfig);

    // add warning messages, with their translation
    if (colsGeneralData.messages) {
      Object.keys(colsGeneralData.messages).forEach(key => {
        _.set(parallelPlotConfig, 'localTextAPI.validationWarnings.' + key,
          this.getTranslation(colsGeneralData.messages[key], null));
      });
    }

    // set extracted configuration
    this._widgetData.parallelPlotConfig = parallelPlotConfig;
    //------------------------------------------------------------------------------------------------------------------

    // output parameters can be omitted so actually no charts will be displayed
    let chartsConfig: any;

    if (! _.isEmpty(outputCols)) {

      chartsConfig = {
        data: outputCols
      };

      // set general output parameters
      this.addGeneralConfigAttributes(chartsAttributes, chartsConfig);

      // translate the header (at the output object so original configuration won't be changed)
      // see also: GEN-83591
      chartsConfig['header'] = this.getTranslation(chartsConfig['header']);

    }

    // set extracted configuration
    this._widgetData.chartsConfig = chartsConfig;
    //------------------------------------------------------------------------------------------------------------------

    // indicate that filter's configuration had changed and is valid
    this.internalState.filterConfigLoaded = true;

    // notify the charts widget on the configuration change (should be before updating data and rebuilding the graph)
    updateHandler.externalDataTarget.onConfigChanged(this._widgetData.chartsConfig);

    // re-invoke the rebuild method
    this.initDataHandler();
    //------------------------------------------------------------------------------------------------------------------

  }
  //********************************************************************************************************

  private extractFilterFormData(filterFormData: any): any {

    let formDataObject: any = {};

    _.keys(filterFormData).forEach(key => {

      // read the 'input' parameters, if not read already
      if (filterFormData[key].input) {
        formDataObject.input = _.split(filterFormData[key].input, ',');
      }

      // read the 'output' parameters, if not read already
      if (filterFormData[key].output) {
        formDataObject.output = _.split(filterFormData[key].output, ',');
      }

      if (formDataObject.input && formDataObject.output) {
        return false; // break
      }
    });

    return formDataObject;
  }
  //********************************************************************************************************

  private addGeneralConfigAttributes(configAttrs, outObj) {

    Object.keys(configAttrs).forEach(key => {
      outObj[key] = configAttrs[key];
    });

  }
  //********************************************************************************************************

  private addQueryInputParam(paramId, configObj, outObj, active): any {

    let paramObj = this.addQueryParam(paramId, configObj, outObj, "header");
    if (paramObj) {
      paramObj.active = active;
      if (! active) {
        paramObj.exclude = true;
      }
    }

    return paramObj;
  }
  //********************************************************************************************************

  private addQueryParam(paramId, configObj, outObj, headerAttrName, idAttrName?): any {

      let configParam = configObj[paramId];
      let outParam = outObj[paramId];

      // extract & add the parameter's configuration to the output object data
      if (configParam) {

        // if column data does NOT already exists in outObj - add it.
        // a column may already been added if it is used in both input and output columns
        if (!outParam) {
          outParam = {...configParam}; // clone original config object so it won't be modified outside of the code
          outObj[paramId] = outParam;

          // translate the parameter's label
          outParam[headerAttrName] = this.getTranslation(paramId);

          // add the ID attribute, if requested so
          if (idAttrName) {
            outParam[idAttrName] = paramId;
          }

        }

        // return the new object
        return outParam;
      }

  }
  //========================================================================================================

  private getTranslation(paramId, prefix: string = 'filter.') {

    if (paramId) {

      if (prefix) {
        paramId = prefix + paramId;
      }
      return this.translate.instant(paramId);
    }
  }
  //========================================================================================================

  private setLoader(state: boolean) {

    if (state) {
      this.loaderCounter++;

      if (this.loaderCounter == 1) {
        this.coreActionCreator.setLoader(true);
      }

    } else {

      this.loaderCounter--;

      if (this.loaderCounter == 0) {
        this.coreActionCreator.setLoader(false);
      }

    }

  }
  //========================================================================================================

  private invokeLater( callback: () => void) {

      Observable.of(null)
        .pipe(delay(2), take(1))
        .subscribe(object => {
          callback();
      });

  }
//========================================================================================================

}
